import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useStudyGroups, useJoinStudyGroup, useNotifications, useMarkNotificationRead, useMe, authedFetch, type StudyGroupListItem } from '../lib/api';
import { canScanQr, scanQrCode, roomCodeFromScan } from '../lib/qr';
import { motion, AnimatePresence, springs, Pressable, FadeIn, Stagger, PageTransition, Skeleton } from '../lib/motion';

export function StudyGroupsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useStudyGroups();
  const me = useMe();
  const isPro = me.data?.user.proAccess ?? false;
  const joinMutation = useJoinStudyGroup();
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const groups = data?.groups ?? [];
  const activeGroups = groups.filter((g) => ['lobby', 'countdown', 'active'].includes(g.status));
  const completedGroups = groups.filter((g) => g.status === 'completed');

  // Check for an in-progress session (countdown or active)
  const liveSession = activeGroups.find(
    (g) => g.status === 'countdown' || g.status === 'active',
  );

  const handleJoin = async () => {
    const code = joinCode.trim().toUpperCase();
    if (code.length < 4) return;
    setJoinError('');
    try {
      const result = await joinMutation.mutateAsync(code);
      setJoinCode('');
      navigate(`/study-groups/${result.group.id}/lobby`);
    } catch (err: any) {
      setJoinError(err.message || 'Could not join. Check the code and try again.');
    }
  };

  const handleScan = async () => {
    setJoinError('');
    const value = await scanQrCode();
    if (!value) return;
    const code = roomCodeFromScan(value);
    if (!code) { setJoinError('That QR code is not a study group invite.'); return; }
    try {
      const result = await joinMutation.mutateAsync(code);
      navigate(`/study-groups/${result.group.id}/lobby`);
    } catch (err: any) {
      setJoinError(err.message || 'Could not join from that code.');
    }
  };

  const handleCancel = async (groupId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Cancel this study group?')) return;
    try {
      await authedFetch(`/study-groups/${groupId}`, { method: 'DELETE' });
      qc.invalidateQueries({ queryKey: ['study-groups'] });
    } catch { /* ignore */ }
  };

  if (isLoading) {
    return (
      <div className="max-w-lg mx-auto pb-24 px-4 pt-1 space-y-4">
        <Skeleton className="h-8 w-44" />
        <Skeleton className="h-28 w-full rounded-2xl" />
        <Skeleton className="h-14 w-full rounded-2xl" />
        <Skeleton className="h-36 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <PageTransition className="max-w-lg mx-auto pb-24 space-y-5 px-4">
      <div>
        <h1 className="text-2xl font-bold font-display text-squishy-dark">Study Groups</h1>
        <p className="text-xs text-slate-500 mt-0.5">Live, timed quiz sessions with your study circle.</p>
      </div>

      {/* ── Pending invites ───────────────────────────────────── */}
      <InvitesSection />

      {/* ── Active session banner ──────────────────────────────── */}
      {liveSession && (
        <Pressable
          haptic="medium"
          onClick={() => {
            const dest = liveSession.status === 'active'
              ? `/study-groups/${liveSession.id}/play`
              : `/study-groups/${liveSession.id}/lobby`;
            navigate(dest);
          }}
          className="relative w-full overflow-hidden bg-gradient-to-br from-squishy-tealDark to-squishy-navy text-white rounded-2xl p-5 text-left shadow-pop"
        >
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" aria-hidden />
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-white/80">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-squishy-teal opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-squishy-teal" />
            </span>
            Live now
          </div>
          <div className="text-lg font-bold font-display mt-1.5">Rejoin your session</div>
          <div className="text-sm text-white/80 mt-0.5">
            {liveSession.title} · {liveSession.questionCount} questions
          </div>
        </Pressable>
      )}

      {/* ── Pro gate ──────────────────────────────────────────── */}
      {!isPro && (
        <FadeIn>
          <div className="relative overflow-hidden bg-gradient-to-br from-squishy-navy to-squishy-navySoft rounded-3xl p-6 text-center space-y-3 shadow-card">
            <div className="pointer-events-none absolute -left-10 -bottom-12 h-36 w-36 rounded-full bg-squishy-teal/20 blur-2xl" aria-hidden />
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-squishy-gold/20">
              <svg className="h-6 w-6 text-squishy-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h2 className="text-base font-bold font-display text-white">Study Groups is a Pro feature</h2>
            <p className="text-xs text-white/70 max-w-xs mx-auto leading-relaxed">
              Go head to head with classmates in live, timed quiz sessions. Upgrade to Squishy Pro to create and join study groups.
            </p>
            <Pressable
              haptic="medium"
              onClick={() => navigate('/profile')}
              className="mt-1 inline-block bg-squishy-gold text-squishy-navy text-sm font-bold px-6 py-3 rounded-2xl shadow-soft"
            >
              Upgrade to Pro
            </Pressable>
          </div>
        </FadeIn>
      )}

      {/* ── Create button ─────────────────────────────────────── */}
      {isPro && (
        <Pressable
          haptic="medium"
          onClick={() => navigate('/study-groups/create')}
          className="w-full bg-squishy-primary text-white font-semibold py-3.5 rounded-2xl text-center shadow-soft"
        >
          Create Study Group
        </Pressable>
      )}

      {/* ── Join with code ────────────────────────────────────── */}
      {isPro && (
      <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3 shadow-card">
        <h2 className="font-semibold text-squishy-dark text-sm">Join with a code</h2>
        <form
          onSubmit={(e) => { e.preventDefault(); handleJoin(); }}
          className="flex gap-2"
        >
          <input
            value={joinCode}
            onChange={(e) => { setJoinCode(e.target.value.toUpperCase()); setJoinError(''); }}
            placeholder="ROOM CODE"
            maxLength={8}
            aria-label="Room code"
            className="flex-1 min-w-0 border border-slate-200 bg-slate-50 rounded-xl px-3 py-3 text-base font-mono tracking-[0.25em] text-center uppercase text-squishy-dark placeholder:tracking-widest placeholder:text-slate-300 focus:outline-none focus:border-squishy-teal focus:shadow-glow transition-shadow"
          />
          <Pressable
            type="submit"
            disabled={joinMutation.isPending || joinCode.trim().length < 4}
            className="bg-squishy-primary text-white px-5 py-3 rounded-xl text-sm font-semibold disabled:opacity-40 transition-opacity"
          >
            {joinMutation.isPending ? 'Joining' : 'Join'}
          </Pressable>
        </form>
        <AnimatePresence>
          {joinError && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={springs.snappy}
              className="text-xs text-red-500"
            >
              {joinError}
            </motion.p>
          )}
        </AnimatePresence>
        {canScanQr() && (
          <Pressable
            onClick={handleScan}
            className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-squishy-primary border border-squishy-primary/25 bg-squishy-teal/5 rounded-xl py-3"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4v-4m-4 4l-4-4M4 4h4v4H4V4zm12 0h4v4h-4V4zM4 16h4v4H4v-4z" />
            </svg>
            Scan a group QR code
          </Pressable>
        )}
      </div>
      )}

      {/* ── Active lobbies ────────────────────────────────────── */}
      {activeGroups.filter((g) => g.status === 'lobby').length > 0 && (
        <section className="space-y-2">
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Open lobbies</h2>
          <Stagger className="space-y-2">
            {activeGroups
              .filter((g) => g.status === 'lobby')
              .map((g) => (
                <Stagger.Item key={g.id}>
                  <motion.div
                    whileTap={{ scale: 0.98 }}
                    transition={springs.snappy}
                    onClick={() => navigate(`/study-groups/${g.id}/lobby`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/study-groups/${g.id}/lobby`); }}
                    className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-left shadow-card hover:border-squishy-teal/60 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-squishy-dark truncate">
                          {g.title}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {g.questionCount} questions · {g.memberCount}/{g.memberCap} players · {formatShortDate(g.createdAt)}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 ml-2 shrink-0">
                        <div className="text-xs font-mono font-semibold bg-squishy-teal/10 text-squishy-primary px-2.5 py-1.5 rounded-lg tracking-wider">
                          {g.roomCode}
                        </div>
                        <button
                          onClick={(e) => handleCancel(g.id, e)}
                          className="text-slate-400 hover:text-red-500 transition-colors p-2.5 -m-1"
                          title="Cancel group"
                          aria-label="Cancel group"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </Stagger.Item>
              ))}
          </Stagger>
        </section>
      )}

      {/* ── Recent completed ──────────────────────────────────── */}
      {completedGroups.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Recent sessions</h2>
          <Stagger className="space-y-2">
            {completedGroups.map((g) => (
              <Stagger.Item key={g.id}>
                <Pressable
                  onClick={() => navigate(`/study-groups/${g.id}/results`)}
                  className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-left shadow-card hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-squishy-dark truncate">
                        {g.title}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {g.questionCount} questions · {g.memberCount} players
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-400 shrink-0 ml-3">
                      {formatRelativeTime(g.completedAt)}
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Pressable>
              </Stagger.Item>
            ))}
          </Stagger>
        </section>
      )}

      {/* ── Empty state ───────────────────────────────────────── */}
      {activeGroups.length === 0 && completedGroups.length === 0 && (
        <FadeIn>
          <div className="text-center py-12">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-squishy-teal/10">
              <svg className="h-7 w-7 text-squishy-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-squishy-dark">No study groups yet</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto leading-relaxed">
              Create a group and invite friends to quiz each other in real time.
            </p>
          </div>
        </FadeIn>
      )}
    </PageTransition>
  );
}

// Pending study-group invites (from notifications). Tapping Join enters the
// lobby via the invite's room code and clears the notification.
function InvitesSection() {
  const navigate = useNavigate();
  const { data } = useNotifications(15);
  const joinMutation = useJoinStudyGroup();
  const markRead = useMarkNotificationRead();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [errId, setErrId] = useState<string | null>(null);

  const invites = (data?.notifications ?? []).filter(
    (n: any) => n.type === 'study_group_invite' && !n.read && n.data?.roomCode,
  );
  if (invites.length === 0) return null;

  const accept = async (n: any) => {
    setBusyId(n.id);
    setErrId(null);
    try {
      const result = await joinMutation.mutateAsync(String(n.data.roomCode).toUpperCase());
      markRead.mutate(n.id);
      navigate(`/study-groups/${result.group.id}/lobby`);
    } catch {
      setErrId(n.id);
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-2">
      <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Invites</h2>
      <AnimatePresence initial={false}>
        {invites.map((n: any) => (
          <motion.div
            key={n.id}
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={springs.soft}
            className="bg-white border border-squishy-teal/40 rounded-2xl p-4 flex items-center gap-3 shadow-card"
          >
            <div className="w-10 h-10 rounded-xl bg-squishy-teal/10 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-squishy-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-squishy-dark truncate">{n.body || 'Study group invite'}</div>
              {errId === n.id
                ? <div className="text-[11px] text-red-500 mt-0.5">That group is no longer available.</div>
                : <div className="text-[11px] text-slate-400 mt-0.5">Tap Join to enter the lobby.</div>}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Pressable
                onClick={() => markRead.mutate(n.id)}
                className="text-[11px] font-medium text-slate-400 hover:text-slate-600 px-2.5 py-3 min-h-[44px]"
              >
                Dismiss
              </Pressable>
              <Pressable
                haptic="medium"
                onClick={() => accept(n)}
                disabled={busyId === n.id}
                className="bg-squishy-primary text-white text-xs font-semibold px-4 py-3 min-h-[44px] rounded-xl disabled:opacity-50"
              >
                {busyId === n.id ? 'Joining' : 'Join'}
              </Pressable>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3_600_000);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
